import { periodsOfTheDay, UnauthorizedError } from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

const moveSlotTargetSchema = z.object({
  id: z.string().uuid(),
  from: z.coerce.date(),
  to: z.coerce.date(),
  periodOfTheDay: z.enum(periodsOfTheDay),
  type: z.enum(['fullvisio', 'mixed']),
  officeName: z.string(),
  doctorAttendingName: z.string(),
  retrocessionRate: z.number(),
  withAssistant: z.boolean(),
});

const getMoveSlotOptionsResponseSchema = z.object({
  availableSlots: z.array(moveSlotTargetSchema),
  sourceSlotId: z.string().uuid(),
  sourceSlotDate: z.coerce.date(),
});

export type GetMoveSlotOptionsResponse = z.infer<
  typeof getMoveSlotOptionsResponseSchema
>;

export type GetMoveSlotOptionsPayload = {
  sourceSlotId: string;
  timezone: string;
  reservationId?: string;
};

const getMoveSlotOptions =
  ({ sourceSlotId, timezone, reservationId }: GetMoveSlotOptionsPayload) =>
  async () => {
    return http.agendaSlots
      .getMoveOptions(sourceSlotId, { timezone, reservationId })
      .then<GetMoveSlotOptionsResponse>((response) => {
        const dataToValidate = {
          availableSlots: response.data.map((slot) => {
            return {
              id: slot.id,
              from: slot.attributes.from,
              to: slot.attributes.to,
              periodOfTheDay: slot.attributes.periodOfTheDay,
              type: slot.attributes.type,
              officeName: slot.attributes.officeName,
              doctorAttendingName: slot.attributes.doctorAttendingName,
              retrocessionRate: slot.attributes.retrocessionRate,
              withAssistant: slot.attributes.withAssistant,
            };
          }),
          sourceSlotId: response.meta.sourceSlotId,
          sourceSlotDate: response.meta.sourceSlotDate,
        };
        try {
          return getMoveSlotOptionsResponseSchema.parse(dataToValidate);
        } catch (error) {
          console.error('Zod validation error:', error);
          throw error;
        }
      });
  };

export const useGetMoveSlotOptionsQuery = (
  payload: GetMoveSlotOptionsPayload,
  enabled: boolean = true,
): UseQueryResult<GetMoveSlotOptionsResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getMoveSlotOptions(payload),
    queryKey: [
      'move-slot-options',
      payload.sourceSlotId,
      payload.timezone,
      payload.reservationId,
    ],
    retry: false,
    enabled,
  });
};
